import type { OfferingPrice } from "../entitlements-api";

/**
 * How an offering's price reaches the screen.
 *
 * Four states, not two, because they mean different things to an operator
 * quoting a customer: a price (including a price of zero), no price at all
 * ("talk to us", which is what an unpriced offering in the catalog means), and
 * a price the console cannot render as money. Collapsing the third into the
 * second would quote "talk to us" for something that has a price, and
 * collapsing zero into "no price" would do it for something that is free.
 */
export type PriceDisplay =
  | { readonly kind: "priced"; readonly text: string }
  | { readonly kind: "unpriced" }
  | { readonly kind: "unreadable" };

/** ISO 4217: exactly three uppercase letters. A length check is not validation. */
const CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * Exhaustive by construction: a `Record` keyed on the union fails the build
 * when the catalog gains an interval, rather than rendering an amount whose
 * period the reader cannot see.
 */
const INTERVAL_SUFFIX: Readonly<Record<OfferingPrice["interval"], string>> = {
  month: "/mo",
  year: "/yr",
  one_time: " once",
};

/**
 * A fixed locale rather than the operator's. This is an internal console whose
 * catalog is authored in one language, and a runtime locale would make the
 * same price render differently on two staff machines and differently again in
 * CI.
 */
const LOCALE = "en-US";

/**
 * Format a catalog price for display.
 *
 * The amount is minor units and is NEVER divided by a hardcoded 100: the
 * number of minor-unit digits is currency-dependent (USD 2, JPY 0, BHD 3), so
 * the divisor comes from the same formatter that will render the result.
 * `Intl.NumberFormat` throws on a currency code it does not recognise, and the
 * code reaches us from the API, so the call is guarded.
 */
export function formatOfferingPrice(price: OfferingPrice | null): PriceDisplay {
  if (price === null) return { kind: "unpriced" };
  if (!Number.isInteger(price.amountCents) || price.amountCents < 0) {
    return { kind: "unreadable" };
  }
  if (!CURRENCY_CODE.test(price.currency)) return { kind: "unreadable" };

  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency: price.currency,
    });
  } catch {
    return { kind: "unreadable" };
  }

  const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return {
    kind: "priced",
    text: `${formatter.format(price.amountCents / 10 ** digits)}${INTERVAL_SUFFIX[price.interval]}`,
  };
}
